// Global packages or components
import { useLoaderData, useLocation } from '@remix-run/react'
import { getHire } from '~/sanity'
import {
  buildListingSchemaProducts,
  formatListingFields,
  getMeta,
  getSchema,
  Schema,
} from '~/utils'
import { ProductListing, Breadcrumbs } from '~/components'
import { getListingProducts } from '~/.server'
import { usePageInfo } from '~/hooks'

// Types
import type {
  MetaFunction,
  LoaderFunction,
  LoaderFunctionArgs,
  HeadersFunction,
} from '@remix-run/node'

// Route loader
export const loader: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs) => {
  const env = process.env

  // Sanity fetch
  const data = await getHire({
    dataset: env?.ENVIRONMENT,
    projectId: env?.SANITY_PROJECT,
  })

  // Page not found
  if (!data) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  const dataFormatted = formatListingFields({ ...data })

  // Variant level search here, unlike the category pages which list parents
  const listing = await getListingProducts({
    request,
    productType: 'bulk',
    categoryFilters: data?.filters ?? [],
  })

  // A crawler walking past the last page gets a 404 rather than an empty grid
  if (listing.ok && listing.page > 1 && listing.products.length === 0) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // Return data
  return Response.json({
    title: data?.title,
    seo: data?.listingSeo,
    content: data?.content,
    filters: data?.filters,
    subCategories: dataFormatted?.subCategories,
    banners: dataFormatted?.banners,
    listing,
  })
}


/*
  Without a headers export Remix discards whatever a loader put on the response
  (getDocumentHeaders, headers.js). Stated explicitly rather than left to the
  CDN default, because app/root.tsx attaches a session cookie and a CSRF token
  to every response and a shared cache must never store those. Listing searches
  are cached upstream instead, in .server/brenda/listingCache.
*/
export const headers: HeadersFunction = () => ({
  'Cache-Control': 'private, no-store',
})

// Route meta
export const meta: MetaFunction = ({ matches, data, location }) => {
  return getMeta({
    matches,
    data,
    location,
  })
}

// Route view
export default function Index() {
  const { title, content, subCategories, banners, seo, listing } =
    useLoaderData<typeof loader>()

  const location = useLocation()

  usePageInfo({
    pageType: 'Page',
    pageTitle: 'Hire Products',
  })

  const jsonLd = [
    getSchema({
      type: 'page',
      page: seo,
    }),
    getSchema({
      type: 'category',
      category: {
        title,
        description: seo?.metaDescription ?? null,
        slug: location.pathname,
        products: buildListingSchemaProducts(listing.products),
        startIndex: (listing.page - 1) * listing.pageSize,
      },
    }),
  ]

  return (
    <main>
      <Schema data={jsonLd} />
      <Breadcrumbs path={'/hire-products'} />
      <ProductListing
        banner={banners?.header}
        bannerSidebar={banners?.sidebar}
        subCategories={subCategories}
        content={content}
        title={title}
        listing={listing}
      />
    </main>
  )
}
